// src/components/RandevuTakvimi.js

import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { db } from '../../firebase';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import trLocale from '@fullcalendar/core/locales/tr';
import { CircularProgress, Box, useMediaQuery, useTheme, Typography } from '@mui/material';

const RandevuTakvimi = ({ onEventClick }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        setLoading(true);

        // 1. Randevuları Dinle
        const qAppointments = query(collection(db, "appointments"), orderBy("randevuTarihi", "desc"));
        const unsubAppointments = onSnapshot(qAppointments, (snapApp) => {
            const appEvents = snapApp.docs.map(doc => {
                const data = doc.data();
                const start = data.randevuTarihi?.toDate ? data.randevuTarihi.toDate() : null;
                if (!start) return null;
                const style = getCategoryStyle(data.ilgilenilenHizmet, 'randevu');
                return {
                    id: doc.id,
                    title: ` ${data.musteriAdSoyad} (R)`, // (R) for Randevu
                    start: start,
                    extendedProps: {
                        type: 'randevu',
                        data: { id: doc.id, ...data }
                    },
                    backgroundColor: style.bg,
                    borderColor: style.border,
                    textColor: style.text
                };
            }).filter(Boolean);

            setRawAppointments(appEvents);
        });

        // Uyku Takip Listener
        const qSleep = query(collection(db, "sleepAppointments"), orderBy("olusturmaTarihi", "desc"));
        const unsubSleep = onSnapshot(qSleep, (snapSleep) => {
            const sleepEvents = snapSleep.docs.map(doc => {
                const data = doc.data();
                const start = data.randevuTarihi?.toDate ? data.randevuTarihi.toDate() : null;
                if (!start) return null;
                const style = getCategoryStyle(null, 'uyku');
                return {
                    id: doc.id,
                    title: ` ${data.musteriAdSoyad} (Uyku)`,
                    start: start,
                    extendedProps: {
                        type: 'uyku',
                        data: { id: doc.id, ...data }
                    },
                    backgroundColor: style.bg,
                    borderColor: style.border,
                    textColor: style.text
                };
            }).filter(Boolean);
            setRawSleep(sleepEvents);
        });

        return () => {
            unsubAppointments();
            unsubSleep();
        };
    }, []);

    // State for raw data
    const [rawAppointments, setRawAppointments] = useState([]);
    const [rawSleep, setRawSleep] = useState([]);

    // Merge logic
    useEffect(() => {
        setEvents([...rawAppointments, ...rawSleep]);
        setLoading(false);
    }, [rawAppointments, rawSleep]);


    // Kategoriye Göre Renk Belirleme
    // Kategoriye Göre Renk Belirleme
    const getCategoryStyle = (category, type) => {
        // Uyku Randevuları (Ayrı koleksiyon)
        if (type === 'uyku') return { bg: '#2196f3', text: '#fff', border: '#1976d2' }; // Mavi

        // Standart Randevular
        const cat = category ? category.trim() : '';

        // Hizmet türüne göre renkler
        switch (cat) {
            case 'Cihaz Tamiri':
                return { bg: '#ffeb3b', text: '#000', border: '#fbc02d' }; // Sarı
            case 'Uyku Testi':
            case 'Maske Denemesi':
                return { bg: '#2196f3', text: '#fff', border: '#1976d2' }; // Mavi
            case 'Muayene':
            case 'Kontrol':
                return { bg: '#9c27b0', text: '#fff', border: '#7b1fa2' }; // Mor
            case 'Ödeme':
                return { bg: '#4caf50', text: '#fff', border: '#388e3c' }; // Yeşil
            case 'Diğer':
            default:
                return { bg: '#757575', text: '#fff', border: '#616161' }; // Gri
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Geldi': return theme.palette.success.main;
            case 'Iptal': return theme.palette.error.main;
            case 'Gelmedi': return theme.palette.text.secondary;
            default: return theme.palette.warning.main;
        }
    };

    const handleEventClick = (clickInfo) => {
        const { type, data } = clickInfo.event.extendedProps;
        // Parent component'e (Randevular.js) sadece ID gönderilip orada navigate edilebilir
        // ancak onEventClick şu an direkt data alıyor.
        // Randevular.js'deki handleEventClick fonksiyonunu da güncellememiz gerekebilir,
        // ya da buradan direkt navigate yapabiliriz ama prop'u kullanmak daha doğru.

        // Randevular.js'deki handleEventClick fonksiyonu şu an navigate yapıyor
        // ama sadece /randevu/:id ye gidiyor.
        // Bu yüzden onEventClick'e type bilgisini de geçmemiz lazım veya
        // Randevular.js'yi bypass edip (ki prop olarak geliyor)
        // **Burada parent'in beklediği formatı değiştirmek** yerine,
        // Parent component (Randevular.js) içindeki handleEventClick'i de güncellemeliyiz.
        // Ama şimdilik onEventClick'e nesnenin kendisini + type bilgisini ekleyip gönderelim.

        onEventClick({ ...data, type }); // data içine type ekleyerek gönderiyoruz
    };

    const renderEventContent = (eventInfo) => {
        const { type, data } = eventInfo.event.extendedProps;
        const time = eventInfo.event.start.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

        return (
            <Box sx={{ p: 0.5, overflow: 'hidden', whiteSpace: 'normal', lineHeight: 1.2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                    {time}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '0.85rem' }}>
                    {eventInfo.event.title}
                </Typography>
                {/* Ek Bilgiler */}
                {type === 'randevu' && data.ilgilenilenHizmet && (
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, fontSize: '0.70rem' }}>
                        {data.ilgilenilenHizmet}
                    </Typography>
                )}
                {type === 'uyku' && (
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, fontSize: '0.70rem', fontStyle: 'italic' }}>
                        Uyku/Solunum
                    </Typography>
                )}
            </Box>
        );
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
    }

    return (
        <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
            headerToolbar={isMobile ? {
                left: 'prev,next',
                center: 'title',
                right: ''
            } : {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
            }}
            titleFormat={isMobile ? { year: 'numeric', month: 'short', day: 'numeric' } : undefined}
            events={events}
            locale={trLocale}
            height={isMobile ? "auto" : "75vh"}
            editable={false}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={3}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            buttonText={{
                today: 'Bugün',
                month: 'Ay',
                week: 'Hafta',
                day: 'Gün',
                list: 'Liste'
            }}
            allDayText="Tüm gün"
            noEventsText="Gösterilecek randevu yok"
            windowResizeDelay={100}
        />
    );
};

export default RandevuTakvimi;